import React, {useState} from "react";
import {AlertTriangle, CreditCard, BarChart3, Building2, FileText, HandCoins, Receipt, Wallet2Icon, Wallet} from "lucide-react";
import {useLocation, useNavigate} from "react-router-dom";
import {Box, Typography, useTheme} from "@mui/material";
import {PageHeader, FeedbackSnackbar} from "../../shared/ui";
import {useFinanceiro} from "./useFinanceiro";
import FinanceiroDashboardPage from "./FinanceiroDashboardPage";
import FinanceiroFaturasPage from "./FinanceiroFaturasPage";
import FinanceiroInadimplenciaPage from "./FinanceiroInadimplenciaPage";
import FinanceiroCobrancasPage from "./FinanceiroCobrancasPage";
import FinanceiroBankAccountsTab from "./components/FinanceiroBankAccountsTab";
import FinanceiroBoletosTab from "./components/FinanceiroBoletosTab";
import FinanceiroReceivablesTab from "./components/FinanceiroReceivablesTab";
import FinanceiroBankTab from "./components/FinanceiroBanksTab";
import FinanceiroPaymentMethodsTab from "./components/FinanceiroPaymentMethodsTab";

const MENU_ITEMS = [
    {id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/financeiro/dashboard'},
    {id: 'faturas', label: 'Faturas', icon: FileText, path: '/financeiro/faturas'},
    {id: 'cobrancas', label: 'Cobranças', icon: HandCoins, path: '/financeiro/cobrancas'},
    {id: 'contas-receber', label: 'Contas a Receber', icon: Wallet, path: '/financeiro/contas-receber'},
    {id: 'inadimplencia', label: 'Inadimplência', icon: AlertTriangle, path: '/financeiro/inadimplencia'},
    {id: 'bancos', label: 'Bancos', icon: Building2, path: '/financeiro/bancos'},
    {id: 'contas-bancarias', label: 'Contas Bancárias', icon: CreditCard, path: '/financeiro/contas-bancarias'},
    {id: 'boletos', label: 'Boletos', icon: Receipt, path: '/financeiro/boletos'},
    {id: 'formas-pagamento', label: 'Formas de Pagamento', icon: Wallet2Icon, path: '/financeiro/formas-pagamento'},
];

const getActiveTab = (pathname) => {
    if (pathname.includes('/financeiro/contas-bancarias')) return 'contas-bancarias';
    if (pathname.includes('/financeiro/bancos')) return 'bancos';
    if (pathname.includes('/financeiro/boletos')) return 'boletos';
    if (pathname.includes('/financeiro/contas-receber')) return 'contas-receber';
    if (pathname.includes('/financeiro/inadimplencia')) return 'inadimplencia';
    if (pathname.includes('/financeiro/faturas')) return 'faturas';
    if (pathname.includes('/financeiro/cobrancas')) return 'cobrancas';
    if (pathname.includes('/financeiro/formas-pagamento')) return 'formas-pagamento';
    return 'dashboard';
};

export default function FinanceiroPage({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const financeiro = useFinanceiro();
    const [open, setOpen] = useState(openParent);
    const activeTab = getActiveTab(location.pathname);
    const activeItem = MENU_ITEMS.find((item) => item.id === activeTab);
    const menus = MENU_ITEMS.map((item) => {
        const Icon = item.icon;
        return {
            label: item.label,
            id: item.id,
            selected: activeTab === item.id,
            disabled: false,
            icon: <Icon size={20} />,
            onClick: () => navigate(item.path),
        };
    });

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <PageHeader
                title="Financeiro"
                itemsMenu={menus}
                onHandleMenuOpen={() => null}
                onHandleDrawerToggle={setOpen}
                openParent={open}
            />

            <FeedbackSnackbar feedback={financeiro.feedback} onClose={financeiro.clearFeedback} />

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    mt: 8,
                    backgroundColor: '#f9fafb',
                    minHeight: 'calc(100dvh - 64px)',
                    p: {xs: 2, md: 4},
                }}
            >
                <Box sx={{mb: 3}}>
                    <Typography sx={{fontSize: {xs: '1.75rem', md: '2rem'}, color: '#111827', fontWeight: 700, lineHeight: 1.1}}>
                        {activeItem?.label || 'Financeiro'}
                    </Typography>
                </Box>

                {activeTab === 'dashboard' ? (
                    <FinanceiroDashboardPage
                        dashboard={financeiro.dashboard}
                        inadimplentes={financeiro.inadimplentes}
                        loading={financeiro.loadingDashboard}
                    />
                ) : null}

                {activeTab === 'faturas' ? (
                    <FinanceiroFaturasPage
                        empresas={financeiro.empresas}
                        faturas={financeiro.faturas}
                        faturasPagination={financeiro.faturasPagination}
                        faturasLoading={financeiro.loadingFaturas}
                        selectedFaturaDetails={financeiro.selectedFaturaDetails}
                        detailsLoading={financeiro.loadingDetails}
                        saving={financeiro.saving}
                        statusFilter={financeiro.statusFilter}
                        page={financeiro.page}
                        onChangeStatusFilter={financeiro.setStatusFilter}
                        onChangePage={financeiro.setPage}
                        onOpenDetails={financeiro.openFaturaDetails}
                        onCloseDetails={financeiro.closeFaturaDetails}
                        onCreateFatura={financeiro.createFatura}
                        onReprocessInvoice={financeiro.reprocessInvoice}
                        onNotifyCollection={financeiro.notifyCollection}
                        faturaParaAbrir={new URLSearchParams(location.search).get('fatura')}
                        onChanged={async (faturaId) => {
                            await financeiro.reloadFaturas();
                            if (faturaId) await financeiro.openFaturaDetails(faturaId);
                        }}
                    />
                ) : null}

                {activeTab === 'cobrancas' ? (
                    <FinanceiroCobrancasPage
                        empresas={financeiro.empresas}
                        aoMudar={financeiro.reloadFaturas}
                        onAbrirFatura={(faturaId) => navigate(`/financeiro/faturas?fatura=${faturaId}`)}
                    />
                ) : null}

                {activeTab === 'inadimplencia' ? (
                    <FinanceiroInadimplenciaPage
                        inadimplentes={financeiro.inadimplentes}
                        inadimplenciaLoading={financeiro.loadingInadimplencia}
                        onBlockLicense={financeiro.blockLicense}
                        onReactivateLicense={financeiro.reactivateLicense}
                        onNotifyCollection={financeiro.notifyCollection}
                    />
                ) : null}

                {activeTab === 'bancos' ? (
                    <FinanceiroBankTab
                        items={financeiro.banks}
                        pagination={financeiro.banksPagination}
                        loading={financeiro.banksLoading}
                        saving={financeiro.saving}
                        search={financeiro.bankSearch}
                        page={financeiro.bankPage}
                        onChangeSearch={financeiro.setBankSearch}
                        onChangePage={financeiro.setBankPage}
                        onSave={financeiro.saveBank}
                        onDelete={financeiro.removeBank}
                    />
                ) : null}

                {activeTab === 'formas-pagamento' ? (
                    <FinanceiroPaymentMethodsTab
                        items={financeiro.paymentMethods}
                        pagination={financeiro.paymentMethodsPagination}
                        loading={financeiro.paymentMethodsLoading}
                        saving={financeiro.saving}
                        search={financeiro.paymentMethodsSearch}
                        page={financeiro.paymentMethodsPage}
                        onChangeSearch={financeiro.setPaymentMethodsSearch}
                        onChangePage={financeiro.setPaymentMethodsPage}
                        onSave={financeiro.savePaymentMethods}
                        onDelete={financeiro.removePaymentMethods}
                    />
                ) : null}

                {activeTab === 'contas-bancarias' ? (
                    <FinanceiroBankAccountsTab
                        items={financeiro.bankAccounts}
                        pagination={financeiro.bankAccountsPagination}
                        loading={financeiro.bankAccountsLoading}
                        saving={financeiro.saving}
                        filter={financeiro.bankAccountFilter}
                        search={financeiro.bankAccountSearch}
                        page={financeiro.bankAccountPage}
                        bankOptions={financeiro.bankOptions}
                        bankOptionsLoading={financeiro.bankOptionsLoading}
                        onChangeFilter={financeiro.setBankAccountFilter}
                        onChangeSearch={financeiro.setBankAccountSearch}
                        onChangePage={financeiro.setBankAccountPage}
                        onChangeBankOptionsSearch={financeiro.setBankOptionsSearch}
                        onSave={financeiro.saveBankAccount}
                        onDelete={financeiro.removeBankAccount}
                        onTestConnection={financeiro.validateBankConnection}
                    />
                ) : null}

                {activeTab === 'boletos' ? (
                    <FinanceiroBoletosTab
                        items={financeiro.boletos}
                        pagination={financeiro.boletosPagination}
                        loading={financeiro.boletosLoading}
                        saving={financeiro.saving}
                        companies={financeiro.empresas}
                        bankAccounts={financeiro.bankAccounts}
                        receivables={financeiro.receivables}
                        statusFilter={financeiro.boletosStatusFilter}
                        empresaFilter={financeiro.boletosEmpresaFilter}
                        search={financeiro.boletosSearch}
                        page={financeiro.boletosPage}
                        selectedBoleto={financeiro.selectedBoletoDetails}
                        selectedBoletoHistory={financeiro.selectedBoletoHistory}
                        detailLoading={financeiro.boletoDetailsLoading}
                        onChangeStatusFilter={financeiro.setBoletosStatusFilter}
                        onChangeEmpresaFilter={financeiro.setBoletosEmpresaFilter}
                        onChangeSearch={financeiro.setBoletosSearch}
                        onChangePage={financeiro.setBoletosPage}
                        onOpenDetails={financeiro.openBoletoDetails}
                        onCloseDetails={financeiro.closeBoletoDetails}
                        onCreateBoleto={financeiro.createBoleto}
                        onCancelBoleto={financeiro.cancelBoleto}
                        onResendEmail={financeiro.resendBoletoEmail}
                        onDownloadPdf={financeiro.requestBoletoPdf}
                    />
                ) : null}

                {activeTab === 'contas-receber' ? (
                    <FinanceiroReceivablesTab
                        items={financeiro.receivables}
                        pagination={financeiro.receivablesPagination}
                        loading={financeiro.receivablesLoading}
                        companies={financeiro.empresas}
                        boletos={financeiro.boletos}
                        statusFilter={financeiro.receivablesStatusFilter}
                        empresaFilter={financeiro.receivablesEmpresaFilter}
                        competenciaFilter={financeiro.receivablesCompetenciaFilter}
                        search={financeiro.receivablesSearch}
                        page={financeiro.receivablesPage}
                        selectedReceivable={financeiro.selectedReceivableDetails}
                        selectedReceivableHistory={financeiro.selectedReceivableHistory}
                        onChangeStatusFilter={financeiro.setReceivablesStatusFilter}
                        onChangeEmpresaFilter={financeiro.setReceivablesEmpresaFilter}
                        onChangeCompetenciaFilter={financeiro.setReceivablesCompetenciaFilter}
                        onChangeSearch={financeiro.setReceivablesSearch}
                        onChangePage={financeiro.setReceivablesPage}
                        onOpenDetails={financeiro.openReceivableDetails}
                        onCloseDetails={financeiro.closeReceivableDetails}
                    />
                ) : null}
            </Box>
        </Box>
    );
}
